import { Button, DialogContent, DialogFooter, DialogHeader, DialogTitle, Password } from '@ibsys/kit';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useChangePassword, useMyProfile } from '@/entities/profile';
import {
  PasswordRulesHint,
  useEnglishLayoutInput,
  usePasswordRulesField,
  usePasswordStrengthValidator,
} from '@/entities/users';

export type ResetPasswordDialogPayload = { oldPassword: string; newPassword: string; confirmNewPassword: string };

export type ResetPasswordDialogProps = {
  close: () => void;
};

export const ResetPasswordDialog: FC<ResetPasswordDialogProps> = ({ close }) => {
  const { t } = useTranslation(['common', 'profile']);

  const { data: profile } = useMyProfile();
  const { mutateAsync: changePassword, isPending: isSavingPassword } = useChangePassword();

  const { control, handleSubmit, reset, setError, clearErrors } = useForm<ResetPasswordDialogPayload>({
    defaultValues: { oldPassword: '', newPassword: '', confirmNewPassword: '' },
  });

  const checkPasswordStrength = usePasswordStrengthValidator();
  const { showServerRefusal } = usePasswordRulesField({ control, setError, clearErrors, name: 'newPassword' });
  // Новый пароль и повтор набираются в английской раскладке; текущий — в системной: он задан до правил
  // и может содержать буквы другой раскладки
  const englishLayoutInput = useEnglishLayoutInput();

  const submit = handleSubmit(async ({ oldPassword, newPassword, confirmNewPassword }) => {
    try {
      // Поле формы `confirmNewPassword` маппится в контрактное `approvedPassword` бэка.
      await changePassword({ oldPassword, newPassword, approvedPassword: confirmNewPassword });
    } catch (error) {
      // Диалог остаётся открытым с введёнными паролями
      showServerRefusal(error);
      return;
    }
    reset();
    close();
  });

  return (
    // Пояснения у диалога нет: поля — его содержимое, а не описание, которое зачитывается целиком
    <DialogContent className={'max-w-96'} aria-describedby={undefined}>
      <DialogHeader>
        <DialogTitle>{t('profile:resetPassword')}</DialogTitle>
      </DialogHeader>
      <form
        className={'flex flex-col gap-3'}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Password
          label={t('profile:currentPassword')}
          control={control}
          name={'oldPassword'}
          // Текущий пароль входит в правила нового: его правка перепроверяет новый
          rules={{ deps: 'newPassword' }}
        />
        <Password
          {...englishLayoutInput}
          label={
            <>
              {t('profile:newPassword')}
              <PasswordRulesHint />
            </>
          }
          control={control}
          name={'newPassword'}
          rules={{
            // Владелец пароля — текущий пользователь, текущий пароль — введённый в первое поле
            validate: (password, { oldPassword }) =>
              checkPasswordStrength(password, {
                email: profile?.email,
                firstName: profile?.firstName,
                lastName: profile?.lastName,
                currentPassword: oldPassword,
              }),
          }}
        />
        <Password
          {...englishLayoutInput}
          label={t('profile:repeatPassword')}
          control={control}
          name={'confirmNewPassword'}
        />
        <DialogFooter className={'flex gap-3'}>
          <Button type={'button'} variant={'ghost'} onClick={close}>
            {t('cancel')}
          </Button>
          <Button type={'submit'} isLoading={isSavingPassword}>
            {t('save')}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
};
